// =============================================================
// Renders the global toast stack + confirm dialog driven by ChromeContext.
// Ports the prototype ToastHost / ConfirmHost. Esc closes the confirm.
// =============================================================

import { useEffect } from "react";
import { useChrome } from "../app/ChromeContext.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { Btn, Icon, ICONS } from "./primitives.js";

const TOAST_ICON: Record<string, string> = {
  success: ICONS.check,
  warning: ICONS.alert,
  error: ICONS.x,
  info: ICONS.gauge,
};

export function ChromeHost() {
  const { toasts, dismissToast, pendingConfirm } = useChrome();
  const t = useScopedT();

  useEffect(() => {
    if (!pendingConfirm) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") pendingConfirm.resolve(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pendingConfirm]);

  return (
    <>
      <div className="toast-host" aria-live="polite">
        {toasts.map((t2) => (
          <div className={`toast ${t2.kind}`} key={t2.id} role="status" onClick={() => dismissToast(t2.id)}>
            <Icon d={TOAST_ICON[t2.kind] ?? ICONS.check} size={16} sw={2.4} />
            <span>{t2.text}</span>
          </div>
        ))}
      </div>

      {pendingConfirm && (
        <div className="modal-scrim" onClick={() => pendingConfirm.resolve(false)}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label={pendingConfirm.title ?? "Confirm"}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-hd">
              <div className="ic">
                <Icon d={pendingConfirm.danger === false ? ICONS.gauge : ICONS.alert} size={20} />
              </div>
              <h3>{pendingConfirm.title ?? "Are you sure?"}</h3>
            </div>
            <div className="modal-body">{pendingConfirm.body ?? "This action cannot be undone."}</div>
            <div className="modal-foot">
              <Btn variant="ghost" size="sm" onClick={() => pendingConfirm.resolve(false)}>
                {pendingConfirm.cancelLabel ?? t("common.cancel")}
              </Btn>
              <Btn
                variant={pendingConfirm.danger === false ? "primary" : "stop"}
                size="sm"
                onClick={() => pendingConfirm.resolve(true)}
              >
                {pendingConfirm.confirmLabel ?? "Confirm"}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
